import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageLayout from "@/components/PageLayout";
import SEO from "@/components/SEO";
import FAQSchema from "@/components/FAQSchema";
import ContactModal from "@/components/new-landing/ContactModal";
import { trackEvent } from "@/utils/analytics";

const NAVY = "#002178";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const triggers = [
  "Your product isn't driving revenue growth",
  "The board has lost confidence in your product direction",
  "You're preparing for a PE exit and need product due diligence",
  "You've outgrown your VP of Product but can't justify a full-time CPO",
  "Product team execution is slow and unfocused",
  "You need senior product leadership during a transition",
  "You're a PE firm and need product diligence across portfolio companies",
  "You've tried consultants and got slides instead of results",
];

const comparisonRows = [
  {
    label: "Time to start",
    fractional: "1-2 weeks",
    fullTime: "3-6 months",
    consultant: "2-4 weeks",
  },
  {
    label: "Engagement",
    fractional: "Embedded, interim",
    fullTime: "Full-time",
    consultant: "Project-based",
  },
  {
    label: "Depth",
    fractional: "Deep, embedded",
    fullTime: "Deep, embedded",
    consultant: "Surface, advisory",
  },
  {
    label: "Cost",
    fractional: "Engagement-based",
    fullTime: "Salary + equity + benefits + recruiting",
    consultant: "Project-based, premium rates",
  },
  {
    label: "Deliverable",
    fractional: "Measurable business impact + team growth",
    fullTime: "Long-term leadership",
    consultant: "Reports + recommendations",
  },
  {
    label: "Best for",
    fractional:
      "Companies needing measurable product-driven growth without full-time overhead",
    fullTime: "Companies ready for permanent C-suite expansion",
    consultant:
      "Companies needing a specific strategic question answered",
  },
];

const steps = [
  {
    number: "01",
    title: "Assessment",
    timeline: "2-4 weeks",
    description:
      "We identify the highest-impact growth levers in your product, team, and market position. You get a prioritized action plan focused on the 5% of work that drives all the impact.",
  },
  {
    number: "02",
    title: "Embedded Leadership",
    timeline: "Ongoing",
    description:
      "We integrate with your team as a senior leader. Setting strategy, coaching PMs, unblocking execution, and driving measurable results.",
  },
  {
    number: "03",
    title: "Transition or Extend",
    timeline: "Flexible",
    description:
      "When the engagement achieves its goals, we either transition leadership to your team or extend the engagement to tackle the next challenge.",
  },
];

const faqs = [
  {
    question: "What is a fractional CPO and how does it work?",
    answer: "FractionalCPO provides executive-level product leadership on an interim or embedded basis. We go further than a solo operator: we embed a team of senior product leaders with partner oversight, accountable for delivering measurable business impact. Engagements typically start within 1-2 weeks."
  },
  {
    question: "What is the difference between FractionalCPO and a fractional CTO?",
    answer: "FractionalCPO focuses on product strategy, user growth, and business outcomes. A fractional CTO focuses on technology architecture, engineering teams, and technical infrastructure. Many PE-backed companies need both, but product leadership drives revenue more directly."
  },
  {
    question: "How much does FractionalCPO cost compared to a full-time hire?",
    answer: "The question isn't what FractionalCPO costs. The question is what it costs to make the wrong leadership hire. Three quarters of no results. A board that's lost confidence. A CFO questioning every product investment. FractionalCPO eliminates that risk. We deliver results in weeks, not quarters. Contact us to discuss your situation."
  },
  {
    question: "When should a company hire FractionalCPO instead of a full-time CPO?",
    answer: "When you need senior product leadership now but can't wait 3-6 months to recruit. When the scope doesn't yet justify a full-time C-suite hire. When you're preparing for a PE exit and need experienced product leadership on a defined timeline. Or when previous product leadership hasn't delivered results. FractionalCPO starts delivering in weeks, not months."
  },
  {
    question: "Is FractionalCPO right for PE-backed companies?",
    answer: "PE-backed companies are our most common clients. The FractionalCPO model fits the PE timeline perfectly: fast start, measurable impact within months, and flexible exit. We've delivered proven, measurable results at PE-backed technology companies."
  },
  {
    question: "What does FractionalCPO do day-to-day?",
    answer: "Sets product strategy, mentors and coaches product managers, aligns product work with business targets, restructures teams when needed, runs growth experiments, and drives measurable outcomes. FractionalCPO operates as embedded members of your leadership team, not outside advisors."
  },
  {
    question: "How is FractionalCPO different from a management consultant?",
    answer: "Management consultants advise and deliver reports. FractionalCPO embeds with your team, takes interim leadership responsibility, makes hard decisions, ships product, and is accountable for results. We don't present findings. We drive outcomes."
  },
  {
    question: "What results can FractionalCPO deliver?",
    answer: "We reversed a decade of decline at a PE-backed platform, delivering doubled product conversion, measurable conversion improvements across all channels, and record-breaking growth. Impact depends on starting position, but we consistently deliver measurable improvements within the first quarter. See our case study for details."
  }
];

const WhatIsAFractionalCPO = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);

  return (
    <PageLayout>
      <SEO
        title="What Is a Fractional CPO? Definition, Cost, and When to Hire"
        description="A Fractional CPO provides executive-level product leadership on an interim basis. Learn what they do, what they cost, and when to hire one for your PE-backed company."
        path="/what-is-a-fractional-cpo"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "dateModified": "2026-03-24"
        }}
      />

      {/* Section 1: Hero */}
      <section className="py-24 md:py-40 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <motion.p
            {...fadeUp}
            className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-8 font-medium"
          >
            Definition
          </motion.p>

          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-[clamp(2.5rem,8vw,6rem)] font-light leading-[0.95] tracking-[-0.03em] mb-12"
          >
            What Is a{" "}
            <span className="text-[#002178] font-medium">
              Fractional CPO?
            </span>
          </motion.h1>

          {/* Definition Block - visually distinct for AEO extraction */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-3xl mb-12 border-l-4 border-[#002178] bg-[#002178]/[0.03] pl-8 pr-6 py-6"
          >
            <p className="text-xl md:text-2xl leading-relaxed font-medium text-[#1a1a1a]">
              FractionalCPO provides executive-level product leadership on an
              interim basis. We go further than a solo operator: we embed a team of
              senior product leaders with partner oversight, accountable for
              delivering measurable business impact.
            </p>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="max-w-2xl"
          >
            <p className="text-lg text-black/60 leading-relaxed mb-4">
              The model exists because most companies need senior product
              leadership long before they can justify (or find) a full-time CPO.
              The gap between a VP of Product and a true Chief Product Officer is
              massive, and that gap costs companies millions in missed revenue,
              misaligned teams, and slow execution.
            </p>
            <p className="text-lg text-black/60 leading-relaxed">
              FractionalCPO serves PE-backed B2B technology companies, growth-stage
              startups, and any organization where product is the primary growth
              lever but leadership hasn't kept pace with the business.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Section 1b: How FractionalCPO is Different */}
      <section
        className="py-24 md:py-32 px-6 md:px-12 lg:px-24"
        style={{ backgroundColor: NAVY }}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-start">
            <div>
              <motion.p
                {...fadeUp}
                className="text-sm uppercase tracking-[0.3em] text-white/50 mb-4 font-medium"
              >
                The FractionalCPO Difference
              </motion.p>
              <motion.h2
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] text-white mb-8"
              >
                We're not a solo contractor.
                <br />
                <span className="font-medium">We're a firm that delivers impact.</span>
              </motion.h2>
              <motion.p
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-white/60 leading-relaxed mb-8"
              >
                Most fractional executives are independent operators. FractionalCPO
                is different. A decade of battle-tested playbooks, not theoretical frameworks. We deploy a team
                of senior product leaders, backed by partner oversight, and we're
                accountable for results, not hours.
              </motion.p>
              <motion.p {...fadeUp} transition={{ duration: 0.6, delay: 0.3 }} className="text-white/60">
                <Link
                  to="/case-study"
                  className="text-white underline underline-offset-4 hover:text-white/80 transition-colors"
                >
                  See our case study &rarr;
                </Link>
              </motion.p>
            </div>

            <div className="space-y-0">
              {[
                {
                  number: "01",
                  title: "A team, not a solo contractor",
                  description:
                    "You get a team of senior product leaders with deep specialization, not one person juggling multiple clients.",
                },
                {
                  number: "02",
                  title: "Partner oversight on every engagement",
                  description:
                    "A founding partner stays close to every engagement, ensuring quality, accountability, and strategic alignment.",
                },
                {
                  number: "03",
                  title: "Measurable impact, proven results",
                  description:
                    "Doubled product conversion. Record-breaking growth. Measurable conversion improvements. We measure what matters and deliver it.",
                },
                {
                  number: "04",
                  title: "Embedded leaders, not sideline advisors",
                  description:
                    "We embed with your team, make decisions, ship product, and own outcomes. No slide decks. No 90-day discovery.",
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  {...fadeUp}
                  transition={{ delay: i * 0.1 }}
                  className="group py-8 border-b border-white/10"
                >
                  <div className="flex gap-6 items-baseline">
                    <span className="text-white/20 font-mono text-sm">
                      {item.number}
                    </span>
                    <div>
                      <h3 className="text-xl md:text-2xl font-medium mb-2 text-white group-hover:text-white/80 transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-white/50 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: When to Hire */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F5F5F0]">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-start">
            <div className="md:sticky md:top-32">
              <motion.h2
                {...fadeUp}
                className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8"
              >
                When should you hire a{" "}
                <span className="text-[#002178] font-medium">
                  Fractional CPO?
                </span>
              </motion.h2>
              <motion.p
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-black/60 leading-relaxed"
              >
                If any of these sound familiar, it's time to talk.
              </motion.p>
            </div>

            <div className="space-y-0">
              {triggers.map((trigger, i) => (
                <motion.div
                  key={i}
                  {...fadeUp}
                  transition={{ delay: i * 0.08 }}
                  className="group py-6 border-b border-black/10"
                >
                  <div className="flex gap-6 items-baseline">
                    <span className="text-[#002178]/30 font-mono text-sm flex-shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-lg font-medium group-hover:text-[#002178] transition-colors">
                      {trigger}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Comparison Table */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <motion.h2
            {...fadeUp}
            className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-16"
          >
            Fractional CPO vs.{" "}
            <span className="text-[#002178] font-medium">
              the Alternatives
            </span>
          </motion.h2>

          {/* Desktop Table */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="hidden md:block overflow-x-auto"
          >
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-black/10">
                  <th className="text-left py-4 pr-6 text-sm uppercase tracking-[0.2em] text-black/40 font-medium w-[15%]" />
                  <th className="text-left py-4 px-4 text-sm uppercase tracking-[0.2em] text-[#002178] font-medium w-[28.3%]">
                    Fractional CPO
                  </th>
                  <th className="text-left py-4 px-4 text-sm uppercase tracking-[0.2em] text-black/40 font-medium w-[28.3%]">
                    Full-Time CPO
                  </th>
                  <th className="text-left py-4 px-4 text-sm uppercase tracking-[0.2em] text-black/40 font-medium w-[28.3%]">
                    Consultant
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr
                    key={i}
                    className="border-b border-black/5 hover:bg-black/[0.02] transition-colors"
                  >
                    <td className="py-5 pr-6 text-sm font-medium text-black/60">
                      {row.label}
                    </td>
                    <td className="py-5 px-4 text-sm font-medium text-[#1a1a1a]">
                      {row.fractional}
                    </td>
                    <td className="py-5 px-4 text-sm text-black/50">
                      {row.fullTime}
                    </td>
                    <td className="py-5 px-4 text-sm text-black/50">
                      {row.consultant}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-6">
            {comparisonRows.map((row, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ delay: i * 0.08 }}
                className="border-b border-black/10 pb-6"
              >
                <p className="text-sm font-medium text-black/40 uppercase tracking-[0.15em] mb-3">
                  {row.label}
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-[#002178]/[0.04] p-3 rounded">
                    <p className="text-xs text-[#002178] font-medium mb-1">
                      Fractional CPO
                    </p>
                    <p className="text-sm font-medium">{row.fractional}</p>
                  </div>
                  <div className="p-3">
                    <p className="text-xs text-black/40 font-medium mb-1">
                      Full-Time CPO
                    </p>
                    <p className="text-sm text-black/60">{row.fullTime}</p>
                  </div>
                  <div className="p-3">
                    <p className="text-xs text-black/40 font-medium mb-1">
                      Consultant
                    </p>
                    <p className="text-sm text-black/60">{row.consultant}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4: What It Costs */}
      <section
        className="py-24 md:py-32 px-6 md:px-12 lg:px-24"
        style={{ backgroundColor: NAVY }}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="max-w-3xl">
            <motion.h2
              {...fadeUp}
              className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] text-white mb-12"
            >
              What does FractionalCPO{" "}
              <span className="font-medium">cost?</span>
            </motion.h2>

            <motion.p
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-lg text-white/70 leading-relaxed mb-6"
            >
              The question isn't what FractionalCPO costs. The question is what it costs to make the wrong leadership hire. Three quarters of no results. A board that's lost confidence. A CFO questioning every product investment. Customers churning while your team debates the roadmap.
            </motion.p>

            <motion.p
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-white font-medium leading-relaxed mb-12"
            >
              FractionalCPO eliminates that risk. We deliver results in weeks, not quarters. Contact us to discuss your situation.
            </motion.p>

            <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.3 }}>
              <button
                onClick={() => { trackEvent('contact_click', { source: 'risk_section' }); setIsContactOpen(true); }}
                className="inline-block px-8 py-4 bg-white text-[#002178] font-medium hover:scale-105 transition-transform"
              >
                Let's chat
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 5: How It Works */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-start">
            <div className="md:sticky md:top-32">
              <motion.h2
                {...fadeUp}
                className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8"
              >
                How a FractionalCPO engagement{" "}
                <span className="text-[#002178] font-medium">works</span>
              </motion.h2>
              <motion.p
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-black/60 leading-relaxed mb-8"
              >
                No lengthy procurement process. No months of onboarding. We've
                done this before and we move fast.
              </motion.p>
              <motion.div
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <button
                  onClick={() => { trackEvent('contact_click', { source: 'how_it_works_section' }); setIsContactOpen(true); }}
                  className="inline-block px-8 py-4 bg-[#002178] text-white font-medium hover:scale-105 transition-transform"
                >
                  Let's chat
                </button>
              </motion.div>
            </div>

            <div className="space-y-0">
              {steps.map((step, i) => (
                <motion.div
                  key={i}
                  {...fadeUp}
                  transition={{ delay: i * 0.1 }}
                  className="group py-8 border-b border-black/10"
                >
                  <div className="flex gap-6 items-baseline">
                    <span className="text-[#002178]/30 font-mono text-sm">
                      {step.number}
                    </span>
                    <div>
                      <h3 className="text-xl md:text-2xl font-medium mb-1 group-hover:text-[#002178] transition-colors">
                        {step.title}
                      </h3>
                      <p className="text-sm text-[#002178]/60 mb-2">
                        {step.timeline}
                      </p>
                      <p className="text-black/50 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: FAQ */}
      <div className="bg-[#F5F5F0]">
        <FAQSchema faqs={faqs} heading="Frequently Asked Questions" />
      </div>

      <ContactModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </PageLayout>
  );
};

export default WhatIsAFractionalCPO;
